import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Building2,
  Plus,
  Search,
  Edit2,
  Trash2,
  Power,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Users,
} from 'lucide-react'
import { workCenterService } from '../../services/workCenterService'
import {
  WorkCenter,
  WorkCenterType,
  WORK_CENTER_LABELS,
} from '../../types'
import { ConfirmDialog, useConfirmDialog } from '../../components/ui/ConfirmDialog'
import { extractApiErrors } from '../../utils/errorHandler'

// Modal de formulario para crear/editar
interface WorkCenterFormModalProps {
  workCenter: WorkCenter | null
  onClose: () => void
  onSuccess: () => void
}

function WorkCenterFormModal({ workCenter, onClose, onSuccess }: WorkCenterFormModalProps) {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    type: workCenter?.type || ('' as WorkCenterType),
    name: workCenter?.name || '',
    fixedStaff: workCenter?.fixedStaff || 100,
    maxConcurrent: workCenter?.maxConcurrent || 5,
    maxPerPosition: workCenter?.maxPerPosition || 3,
    maxPerDay: workCenter?.maxPerDay || 5,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (workCenter) {
        await workCenterService.update(workCenter.id, {
          name: formData.name,
          fixedStaff: formData.fixedStaff,
          maxConcurrent: formData.maxConcurrent,
          maxPerPosition: formData.maxPerPosition,
          maxPerDay: formData.maxPerDay,
        })
        toast.success('Centro de trabajo actualizado')
      } else {
        await workCenterService.create(formData)
        toast.success('Centro de trabajo creado')
      }
      onSuccess()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-dark-card rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-6">
            {workCenter ? 'Editar Centro de Trabajo' : 'Nuevo Centro de Trabajo'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!workCenter && (
              <div>
                <label className="label">Tipo</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as WorkCenterType })}
                  className="input w-full"
                  required
                >
                  <option value="">Seleccionar tipo</option>
                  {Object.entries(WORK_CENTER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="label">Nombre</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                placeholder="Ej: Nave 1"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Plantilla fija</label>
                <input
                  type="number"
                  value={formData.fixedStaff}
                  onChange={(e) => setFormData({ ...formData, fixedStaff: parseInt(e.target.value) || 0 })}
                  className="input w-full"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="label">Max. concurrente</label>
                <input
                  type="number"
                  value={formData.maxConcurrent}
                  onChange={(e) => setFormData({ ...formData, maxConcurrent: parseInt(e.target.value) || 0 })}
                  className="input w-full"
                  min="1"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">2.5% plantilla (min. 5)</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Max. por puesto</label>
                <input
                  type="number"
                  value={formData.maxPerPosition}
                  onChange={(e) => setFormData({ ...formData, maxPerPosition: parseInt(e.target.value) || 0 })}
                  className="input w-full"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="label">Max. por dia</label>
                <input
                  type="number"
                  value={formData.maxPerDay}
                  onChange={(e) => setFormData({ ...formData, maxPerDay: parseInt(e.target.value) || 0 })}
                  className="input w-full"
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Guardando...' : workCenter ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export function WorkCentersPage() {
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterActive, setFilterActive] = useState<boolean | ''>('')
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingWorkCenter, setEditingWorkCenter] = useState<WorkCenter | null>(null)
  const [seeding, setSeeding] = useState(false)

  const confirmDialog = useConfirmDialog()

  useEffect(() => {
    loadWorkCenters()
  }, [])

  const loadWorkCenters = async () => {
    try {
      setLoading(true)
      const response = await workCenterService.getAll()
      setWorkCenters(response.workCenters)
    } catch (error) {
      toast.error('Error al cargar los centros de trabajo')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleSeedDefaults = async () => {
    const confirmed = await confirmDialog.confirm({
      title: 'Cargar centros por defecto',
      message: 'Se crearán los centros de trabajo predeterminados. Los centros existentes con el mismo tipo no se duplicarán.',
      confirmText: 'Cargar',
      cancelText: 'Cancelar',
      variant: 'primary',
    })

    if (!confirmed) return

    try {
      setSeeding(true)
      const result = await workCenterService.seedDefaults()
      toast.success(`${result.created} centros creados correctamente`)
      loadWorkCenters()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    } finally {
      setSeeding(false)
    }
  }

  const handleToggleActive = async (wc: WorkCenter) => {
    try {
      const updated = await workCenterService.toggleActive(wc.id)
      toast.success(`Centro ${updated.isActive ? 'activado' : 'desactivado'}`)
      loadWorkCenters()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    }
  }

  const handleDelete = async (wc: WorkCenter) => {
    const confirmed = await confirmDialog.confirm({
      title: 'Eliminar centro de trabajo',
      message: `Esta seguro de eliminar "${wc.name}"? Esta accion no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    })

    if (!confirmed) return

    try {
      await workCenterService.delete(wc.id)
      toast.success('Centro de trabajo eliminado')
      loadWorkCenters()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    }
  }

  const handleEdit = (wc: WorkCenter) => {
    setEditingWorkCenter(wc)
    setShowFormModal(true)
  }

  const handleCreate = () => {
    setEditingWorkCenter(null)
    setShowFormModal(true)
  }

  const handleFormSuccess = () => {
    setShowFormModal(false)
    setEditingWorkCenter(null)
    loadWorkCenters()
  }

  // Filter work centers
  const filteredWorkCenters = workCenters.filter((wc) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      if (!wc.name.toLowerCase().includes(search) && !wc.type.toLowerCase().includes(search)) {
        return false
      }
    }
    if (filterActive !== '' && wc.isActive !== filterActive) return false
    return true
  })

  return (
    <div className="animate-slide-in">
      <div className="page-header">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              <span className="hidden sm:inline">Centros de Trabajo</span>
              <span className="sm:hidden">Centros</span>
            </h1>
            <p className="page-subtitle">
              Gestiona los centros de trabajo y sus limites
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSeedDefaults}
              disabled={seeding}
              className="btn btn-outline flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${seeding ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Cargar por defecto</span>
              <span className="sm:hidden">Cargar</span>
            </button>
            <button
              onClick={handleCreate}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nuevo Centro</span>
              <span className="sm:hidden">Nuevo</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          <select
            value={filterActive === '' ? '' : filterActive ? 'true' : 'false'}
            onChange={(e) =>
              setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')
            }
            className="input sm:w-40"
          >
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Work Centers List */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        </div>
      ) : filteredWorkCenters.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No hay centros de trabajo configurados
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Carga los centros por defecto o crea uno nuevo
            </p>
            <button onClick={handleSeedDefaults} className="btn btn-primary">
              Cargar centros por defecto
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredWorkCenters.map((wc) => (
            <div
              key={wc.id}
              className={`card ${!wc.isActive ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                      {wc.type}
                    </span>
                    <span
                      className={`badge text-xs ${wc.isActive ? 'badge-success' : 'badge-gray'}`}
                    >
                      {wc.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {wc.name}
                  </h3>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleActive(wc)}
                    className={`p-2 rounded-lg transition-colors ${
                      wc.isActive
                        ? 'text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20'
                        : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={wc.isActive ? 'Desactivar' : 'Activar'}
                  >
                    <Power className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(wc)}
                    className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(wc)}
                    className="p-2 rounded-lg text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Plantilla fija:</span>
                  <span className="font-medium">{wc.fixedStaff}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Max. concurrente:</span>
                  <span className="font-medium">{wc.maxConcurrent}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Max. por puesto:</span>
                  <span className="font-medium">{wc.maxPerPosition}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Max. por dia:</span>
                  <span className="font-medium">{wc.maxPerDay}</span>
                </div>
              </div>

              {wc.employeeCount !== undefined && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Users className="h-4 w-4" />
                    <span>{wc.employeeCount} empleados asignados</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Stats summary */}
      {!loading && workCenters.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary-100 dark:bg-primary-900/30">
              <Building2 className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {workCenters.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total de centros
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-success-100 dark:bg-success-900/30">
              <CheckCircle className="h-6 w-6 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {workCenters.filter((wc) => wc.isActive).length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Centros activos
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {workCenters.reduce((sum, wc) => sum + (wc.employeeCount || 0), 0)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Empleados totales
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <WorkCenterFormModal
          workCenter={editingWorkCenter}
          onClose={() => {
            setShowFormModal(false)
            setEditingWorkCenter(null)
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog.dialogProps} />
    </div>
  )
}
